import { ExternalLink } from "lucide-react";

const gov = (q) => `https://www.gov.uk/search/all?keywords=${encodeURIComponent(q)}`;

export const LEGISLATION = [
  ["Children and Families Act 2014", "https://www.legislation.gov.uk/ukpga/2014/6/contents", "Part 3 sets out duties for children and young people with SEND, including EHC plans."],
  ["SEND Code of Practice: 0 to 25 years", gov("SEND code of practice 0 to 25 years"), "Statutory guidance on identifying, assessing and supporting SEND."],
  ["SEND Regulations 2014", "https://www.legislation.gov.uk/uksi/2014/1530/contents", "Regulations on assessments, EHC plans, reviews and the SEN Information Report."],
  ["Equality Act 2010", "https://www.legislation.gov.uk/ukpga/2010/15/contents", "Reasonable adjustments and accessibility duties for disabled pupils."],
  ["Data Protection Act 2018", "https://www.legislation.gov.uk/ukpga/2018/12/contents", "UK data protection law, alongside UK GDPR."],
  ["UK GDPR", "https://ico.org.uk/for-organisations/uk-gdpr-guidance-and-resources/", "ICO guidance on handling personal data, including children's data."],
  ["Teachers' Standards", gov("Teachers' Standards"), "Standard 5: adapt teaching to respond to the strengths and needs of all pupils."],
  ["Mandatory SENCO qualification guidance", gov("SENCO mandatory qualification NPQ"), "Requirements for SENCOs to hold the national qualification."],
  ["Education Inspection Framework", gov("Ofsted education inspection framework"), "How Ofsted inspects schools, including provision for pupils with SEND."],
  ["Area SEND Inspection Framework", gov("area SEND inspection framework and handbook"), "Joint Ofsted and CQC inspections of local area SEND arrangements."],
  ["Keeping Children Safe in Education", gov("Keeping children safe in education"), "Statutory safeguarding guidance, including additional vulnerabilities of children with SEND."],
  ["Working Together to Safeguard Children", gov("Working together to safeguard children"), "Multi-agency safeguarding arrangements."],
  ["Working Together to Improve School Attendance", gov("Working together to improve school attendance"), "Attendance expectations, including support for pupils with SEND."],
  ["Alternative Provision guidance", gov("alternative provision statutory guidance"), "Statutory guidance for commissioning alternative provision."],
];

const Section = ({ id, title, children }) => (
  <section id={id} aria-labelledby={`${id}-title`} className="tool-section avoid-break scroll-mt-36 rounded-[1.75rem] border-2 border-line bg-paper p-5 sm:p-8 lg:p-10" data-testid={`tool-${id}`}>
    <h3 id={`${id}-title`} className="mb-5 font-display text-2xl font-medium sm:text-3xl">{title}</h3>
    {children}
  </section>
);

export const LegislationSection = () => (
  <Section id="wb-legislation" title="Legislative & guidance alignment">
    <p className="mb-5 text-ink-soft">The workbook's prompts are written with reference to the following. Links open the official source in a new tab.</p>
    <ul className="grid gap-3 md:grid-cols-2">
      {LEGISLATION.map(([name, url, note]) => (
        <li key={name} className="avoid-break rounded-2xl border-2 border-line bg-surface p-4">
          <a href={url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 font-bold underline decoration-2 underline-offset-4">{name}<ExternalLink size={14} aria-hidden /><span className="sr-only">(opens in a new tab)</span></a>
          <p className="mt-1 text-sm text-ink-soft">{note}</p>
          <p className="print-only-url mt-1 break-all text-xs">{url}</p>
        </li>
      ))}
    </ul>
  </Section>
);

export const SourcesAppendix = () => (
  <Section id="wb-sources" title="Official sources appendix">
    <ul className="space-y-2 text-sm">
      {[["GOV.UK guidance", "https://www.gov.uk/education"], ["Legislation.gov.uk", "https://www.legislation.gov.uk"], ["ICO guidance", "https://ico.org.uk"],
        ["SEND Code of Practice", gov("SEND code of practice")], ["Teachers' Standards", gov("Teachers' Standards")],
        ["Ofsted frameworks", "https://www.gov.uk/government/organisations/ofsted"], ["SEND inspection frameworks", gov("area SEND inspection framework")]].map(([n, u]) => (
        <li key={n}><span className="font-bold">{n}:</span> <a href={u} target="_blank" rel="noopener noreferrer" className="break-all underline">{u}</a></li>
      ))}
    </ul>
    <p className="mt-4 text-xs text-ink-soft">Always check the official source for the latest version before making decisions.</p>
  </Section>
);

export const AboutWorkbook = () => (
  <Section id="wb-about" title="Accessibility, privacy and disclaimer">
    <div className="grid gap-5 md:grid-cols-3">
      <div>
        <h4 className="mb-2 font-bold">Accessibility statement</h4>
        <p className="text-sm leading-relaxed">Designed for accessible PDFs, screen readers and keyboard navigation, with high-contrast design, plain English, clear headings and a print-friendly layout. Use the accessibility controls in the top bar for larger text, a dyslexia-friendly font, high contrast, reduced motion and read-aloud.</p>
      </div>
      <div>
        <h4 className="mb-2 font-bold">Privacy notice</h4>
        <p className="text-sm leading-relaxed">No registration or login is required. Feedback is optional. Your answers stay in your browser. Use pseudonymised references (for example Pupil A001) and avoid any identifiable pupil information.</p>
      </div>
      <div>
        <h4 className="mb-2 font-bold">Compliance disclaimer</h4>
        <p className="text-sm leading-relaxed">This workbook supports reflection and improvement planning. It is not legal advice, not consultancy, and completing it is not evidence of statutory compliance.</p>
      </div>
    </div>
  </Section>
);
